import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  TextField
} from '@mui/material';
import {
  Print as PrintIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import AccountsReceivableService from '../../services/accountsReceivableService';
import accountingDataFetchService from '../../services/accountingDataFetchService';

const AccountsReceivableTab = () => {
  // Redux state
  const schooldetails = useSelector(state => state.schooldetails || []);
  const currentSchool = schooldetails.length > 0 ? schooldetails[0] : null;
  
  // Local state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountsReceivableData, setAccountsReceivableData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  
  // Data state
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  
  // Refs to prevent infinite loops
  const isCalculatingRef = useRef(false);

  // Fetch data from database
  const fetchData = async () => {
    if (!currentSchool?.schoolID) {
      setError('No school selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await accountingDataFetchService.fetchAllAccountingData(currentSchool.schoolID);
      setStudents(data.students || []);
      setClasses(data.classes || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate accounts receivable
  useEffect(() => {
    if (isCalculatingRef.current) return;
    if (!currentSchool?.schoolID) return;
    if (students.length === 0) return;

    const calculateAccountsReceivable = () => {
      if (isCalculatingRef.current) return;
      isCalculatingRef.current = true;

      try {
        const summary = AccountsReceivableService.getAccountsReceivableSummary(
          students,
          classes
        );

        setAccountsReceivableData({
          ...summary,
          period: {
            calculated: new Date().toISOString().split('T')[0]
          }
        });
      } catch (err) {
        console.error('Error calculating accounts receivable:', err);
        setError('Failed to calculate accounts receivable');
      } finally {
        isCalculatingRef.current = false;
      }
    };

    calculateAccountsReceivable();
  }, [students, classes, currentSchool?.schoolID]);

  // Load data on mount
  useEffect(() => {
    if (currentSchool?.schoolID) {
      fetchData();
    }
  }, [currentSchool?.schoolID]);

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.00';
    }
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  // Filter debtors
  const filteredDebtors = accountsReceivableData?.debtorsList?.filter(debtor => {
    const matchesSearch = searchTerm === '' || 
      debtor.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      debtor.studentId.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesClass = filterClass === 'all' || debtor.class === filterClass;
    
    return matchesSearch && matchesClass;
  }) || [];

  // Get unique classes for filter
  const uniqueClasses = [...new Set(accountsReceivableData?.debtorsList?.map(d => d.class) || [])].sort();

  if (loading && !accountsReceivableData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && !accountsReceivableData) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchData}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!accountsReceivableData) {
    return (
      <Box>
        <Alert severity="info">No data available. Please refresh.</Alert>
        <Button variant="contained" onClick={fetchData} sx={{ mt: 2 }}>
          Load Data
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Search Student"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filter by Class</InputLabel>
          <Select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            label="Filter by Class"
          >
            <MenuItem value="all">All Classes</MenuItem>
            {uniqueClasses.map(className => (
              <MenuItem key={className} value={className}>{className}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          Refresh Data
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Outstanding
                  </Typography>
                  <Typography variant="h4" color="error">
                    ₵{formatCurrency(accountsReceivableData.totalOutstanding)}
                  </Typography>
                </Box>
                <AccountBalanceIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Number of Debtors
                  </Typography>
                  <Typography variant="h4">
                    {accountsReceivableData.debtorCount}
                  </Typography>
                </Box>
                <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Average Outstanding
              </Typography>
              <Typography variant="h4">
                ₵{formatCurrency(
                  accountsReceivableData.debtorCount > 0
                    ? accountsReceivableData.totalOutstanding / accountsReceivableData.debtorCount
                    : 0
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Last Updated
              </Typography>
              <Typography variant="body1">
                {formatDate(accountsReceivableData.period.calculated)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Discounts Given
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    ₵{formatCurrency(accountsReceivableData.totalDiscountsGiven || 0)}
                  </Typography>
                </Box>
                <TrendingDownIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Aging Analysis */}
      {accountsReceivableData.aging && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Aging Analysis
            </Typography>
          </Box>
          
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="success.main" variant="h5">
                    ₵{formatCurrency(accountsReceivableData.aging.current.amount)}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    Current (0-30 days)
                  </Typography>
                  <Typography variant="caption" color="warning.main" display="block">
                    Discounts: ₵{formatCurrency(accountsReceivableData.aging.current.discounts || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {accountsReceivableData.aging.current.count} students
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="warning.main" variant="h5">
                    ₵{formatCurrency(accountsReceivableData.aging.days30.amount)}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    31-60 days
                  </Typography>
                  <Typography variant="caption" color="warning.main" display="block">
                    Discounts: ₵{formatCurrency(accountsReceivableData.aging.days30.discounts || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {accountsReceivableData.aging.days30.count} students
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="error.main" variant="h5">
                    ₵{formatCurrency(accountsReceivableData.aging.days60.amount)}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    61-90 days
                  </Typography>
                  <Typography variant="caption" color="warning.main" display="block">
                    Discounts: ₵{formatCurrency(accountsReceivableData.aging.days60.discounts || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {accountsReceivableData.aging.days60.count} students
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="error.dark" variant="h5">
                    ₵{formatCurrency(accountsReceivableData.aging.days90.amount)}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    90+ days
                  </Typography>
                  <Typography variant="caption" color="warning.main" display="block">
                    Discounts: ₵{formatCurrency(accountsReceivableData.aging.days90.discounts || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {accountsReceivableData.aging.days90.count} students
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Outstanding by Class */}
      {accountsReceivableData.outstandingByClass && Object.keys(accountsReceivableData.outstandingByClass).length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Outstanding by Class
            </Typography>
          </Box>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Class</strong></TableCell>
                  <TableCell align="right"><strong>Outstanding (₵)</strong></TableCell>
                  <TableCell align="right"><strong>Percentage</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(accountsReceivableData.outstandingByClass)
                  .sort((a, b) => b[1] - a[1])
                  .map(([className, amount]) => {
                    const percentage = accountsReceivableData.totalOutstanding > 0
                      ? ((amount / accountsReceivableData.totalOutstanding) * 100).toFixed(1)
                      : '0.0';
                    
                    return (
                      <TableRow key={className}>
                        <TableCell>{className}</TableCell>
                        <TableCell align="right">
                          <strong>₵{formatCurrency(amount)}</strong>
                        </TableCell>
                        <TableCell align="right">{percentage}%</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Debtors List */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Debtors List ({filteredDebtors.length} of {accountsReceivableData.debtorCount})
          </Typography>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Student Name</strong></TableCell>
                <TableCell><strong>Student ID</strong></TableCell>
                <TableCell><strong>Class</strong></TableCell>
                <TableCell align="right"><strong>Total Due</strong></TableCell>
                <TableCell align="right"><strong>Amount Paid</strong></TableCell>
                  <TableCell align="right"><strong>Discounts</strong></TableCell>
                <TableCell align="right"><strong>Outstanding</strong></TableCell>
                <TableCell><strong>Last Payment</strong></TableCell>
                <TableCell><strong>Contact</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDebtors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="textSecondary">No debtors found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDebtors.map((debtor) => {
                  const daysSinceLastPayment = debtor.lastPaymentDate
                    ? Math.floor((new Date() - new Date(debtor.lastPaymentDate)) / (1000 * 60 * 60 * 24))
                    : null;
                  
                  let agingColor = 'default';
                  if (daysSinceLastPayment !== null) {
                    if (daysSinceLastPayment <= 30) agingColor = 'success';
                    else if (daysSinceLastPayment <= 60) agingColor = 'warning';
                    else if (daysSinceLastPayment <= 90) agingColor = 'error';
                    else agingColor = 'error';
                  }
                  
                  return (
                    <TableRow key={debtor.studentId}>
                      <TableCell>{debtor.studentName}</TableCell>
                      <TableCell>{debtor.studentId}</TableCell>
                      <TableCell>{debtor.class}</TableCell>
                      <TableCell align="right">₵{formatCurrency(debtor.totalDue)}</TableCell>
                      <TableCell align="right">₵{formatCurrency(debtor.amountPaid)}</TableCell>
                      <TableCell align="right">
                        <Typography color="warning.main" fontWeight="bold">
                          ₵{formatCurrency(debtor.discountsGiven || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="error.main" fontWeight="bold">
                          ₵{formatCurrency(debtor.outstanding)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {debtor.lastPaymentDate ? (
                          <Box>
                            <Typography variant="body2">
                              {formatDate(debtor.lastPaymentDate)}
                            </Typography>
                            {daysSinceLastPayment !== null && (
                              <Chip 
                                label={`${daysSinceLastPayment} days`}
                                color={agingColor}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Chip label="No payment" color="error" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{debtor.contact}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AccountsReceivableTab;
